import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.motion/dist/leaflet.motion";
import { isMobile } from "../../../../Constants";
import "leaflet-sidebar/src/L.Control.Sidebar.css";
import "leaflet-sidebar/src/L.Control.Sidebar";
import "leaflet-easybutton";
import india from "../../india.json";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const Map = ({
  inflow = [],
  outflow = [],
  searched = [],
  offsetWidth,
  currentSearchedLocation,
  showSidebar,
  setcurrentLayer,
  detectedCoords,
}) => {
  const mapRef = useRef(null);
  const sidebarRef = useRef(null);
  const markersLayerRef = useRef(new L.LayerGroup());
  const inflowLayerRef = useRef(new L.LayerGroup());
  const outflowLayerRef = useRef(new L.LayerGroup());
  const controlElemsRef = useRef([]);
  const layersControlRef = useRef(null);

  // Resize fix on offsetWidth change
  useEffect(() => {
    const interval = setInterval(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 5000);
    return () => clearInterval(interval);
  }, [offsetWidth]);

  useEffect(() => {
    if (!mapRef.current) {
      initializeMap();
    }
    addTileLayer();
  }, []);

  useEffect(() => {
    if (detectedCoords && mapRef.current) {
      const map = mapRef.current;
      const { lat, lng } = detectedCoords;
      const zoom = detectedCoords.zoom || 5;

      const timer = setTimeout(() => {
        map.invalidateSize();

        const targetPoint = map.project([lat, lng], zoom);
        const offsetPoint = targetPoint.add([window.innerWidth / 5, 0]);
        const targetLatLng = map.unproject(offsetPoint, zoom);

        map.flyTo(targetLatLng, zoom, {
          duration: 2.5,
          animate: true,
        });
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [detectedCoords]);

  const initializeMap = () => {
    const initLat = detectedCoords?.lat || 20.0;
    const initLng = detectedCoords?.lng || 0.0;
    const initZoom = detectedCoords?.zoom || 2;

    mapRef.current = L.map("locationDashboardMap", {
      center: [initLat, initLng],
      zoom: initZoom,
      maxZoom: 18,
      minZoom: 2,
      zoomSnap: 0.5,
      zoomControl: true,
    });
  };

  useEffect(() => {
    if (showSidebar && sidebarRef.current) {
      sidebarRef.current.show();
      const sidebarElem = document.getElementById("leaflet-sidebar-content");
      sidebarElem.style.background = "transparent";
      sidebarElem.style.border = "none";
    } else {
      if (!isMobile() && sidebarRef.current) sidebarRef.current.hide();
    }
  }, [showSidebar]);

  useEffect(() => {
    markersLayerRef.current.clearLayers();
    inflowLayerRef.current.clearLayers();
    outflowLayerRef.current.clearLayers();

    if (inflow.length > 0 || searched.length > 0 || outflow.length > 0) {
      setTimeout(() => {
        plotInbound();
        plotOutbound();
        plotMarkers();
      }, 1200);

      setTimeout(() => {
        controlElemsRef.current = document.getElementsByClassName(
          "leaflet-control-layers-selector",
        );
        if (controlElemsRef.current.length > 0) {
          setcurrentLayer("all");
          const [_, inbound, outbound] = controlElemsRef.current;
          inbound.setAttribute("id", "inbound-layer");
          outbound.setAttribute("id", "outbound-layer");

          inbound.addEventListener("change", (e) =>
            updateLayerToggle(e.target.id, e.target.checked),
          );
          outbound.addEventListener("change", (e) =>
            updateLayerToggle(e.target.id, e.target.checked),
          );
        }
      }, 500);
    }
  }, [inflow, outflow, searched]);

  useEffect(() => {
    if (searched && searched.length === 2 && mapRef.current) {
      const [lng, lat] = searched;
      const map = mapRef.current;

    const getCountryCenter = (lat, lng) => {
      // for India
      if (lat >= 8 && lat <= 37 && lng >= 68 && lng <= 97)
        return { lat: 22.5937, lng: 78.9629, zoom: 4.4 };

      // For all other countries
      return { lat, lng, zoom: 4.5 };
    };

    const { lat: centerLat, lng: centerLng, zoom } = getCountryCenter(lat, lng);

    try {
      map.invalidateSize();
      const targetPoint = map.project([centerLat, centerLng], zoom);
      const offsetPoint = targetPoint.add([window.innerWidth / 5, 0]);
      const targetLatLng = map.unproject(offsetPoint, zoom);
      map.flyTo(targetLatLng, zoom, {
        duration: 1.5,
        animate: true,
      });
    } catch (e) {
      console.warn("Map search error:", e);
    }
    }
  }, [searched]);

  // const initializeMap = () => {
  //   mapRef.current = L.map("locationDashboardMap", {
  //     // center: [22.5937, 78.9629],
  //     center: [-10, 80],
  //     zoom: 8,
  //     maxZoom: 6,
  //     minZoom: 4,
  //     zoomSnap: 0.5,
  //     zoomControl: true,
  //     zoomDelta: 0.5,
  //   });
  // };

  const addTileLayer = () => {
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
      {
        attribution:
          "Esri, HERE, Garmin, &copy; OpenStreetMap contributors, and the GIS user community",
        maxZoom: 19,
      },
    ).addTo(mapRef.current);

    const latLngs = L.GeoJSON.coordsToLatLngs(
      india.features[0].geometry.coordinates,
      2,
    );

    L.polyline(latLngs, {
      color: "#393185",
      weight: 2,
      opacity: 0.2,
    }).addTo(mapRef.current);

    const baseMaps = {
      Locations: markersLayerRef.current,
    };
    const overlayMaps = {
      "Inbound Movement": inflowLayerRef.current,
      "Outbound Movement": outflowLayerRef.current,
    };

    // Remove existing layers control if it exists
    if (layersControlRef.current) {
      mapRef.current.removeControl(layersControlRef.current);
    }

    // Add new legend box and store reference
    layersControlRef.current = L.control
      .layers(baseMaps, overlayMaps, {
        collapsed: isMobile(),
        position: isMobile() ? "topright" : "bottomright",
      })
      .addTo(mapRef.current);

    baseMaps.Locations.addTo(mapRef.current);

    if (!isMobile()) {
      sidebarRef.current = L.control
        .sidebar("leaflet-sidebar-content", {
          position: "right",
          closeButton: false,
        })
        .addTo(mapRef.current);
    }
  };

  const updateLayerToggle = (layer) => {
    const inbound = document.getElementById("inbound-layer")?.checked;
    const outbound = document.getElementById("outbound-layer")?.checked;

    if (layer === "inbound-layer") {
      setcurrentLayer(
        inbound && outbound
          ? "all"
          : inbound
            ? "inbound-layer"
            : outbound
              ? "outbound-layer"
              : "none",
      );
    }

    if (layer === "outbound-layer") {
      setcurrentLayer(
        outbound && inbound
          ? "all"
          : outbound
            ? "outbound-layer"
            : inbound
              ? "inbound-layer"
              : "none",
      );
    }
  };

  const plotMarkers = () => {
    if (searched.length > 0) {
      const [lng, lat] = searched;
      const marker = L.marker([lat, lng]).bindPopup(
        `<b>${currentSearchedLocation}</b>`,
      );
      const circle = L.circle([lat, lng], { radius: 150000 });

      marker.on("click", () => {
        if (!isMobile()) sidebarRef.current?.show();
        else
          document.getElementById("Location-Transporters-Dashboard")?.click();
      });

      markersLayerRef.current
        .addLayer(circle)
        .addLayer(marker)
        .addTo(mapRef.current);
    }
  };

  const plotInbound = () => {
    if (inflow.length > 0 && searched.length > 0) {
      const [lng, lat] = searched;
      inflow.forEach(({ origin_coordinates }) => {
        const originLat = origin_coordinates[1];
        const originLng = origin_coordinates[0];

        const line = L.polyline(
          [
            [originLat, originLng],
            [lat, lng],
          ],
          { color: "#393185", weight: 1.5, opacity: 0.3 },
        );

        inflowLayerRef.current.addLayer(line).addTo(mapRef.current);
      });
    }
  };

  const plotOutbound = () => {
    if (outflow.length > 0 && searched.length > 0) {
      const [lng, lat] = searched;
      outflow.forEach(({ destination_coordinates }) => {
        const destLat = destination_coordinates[1];
        const destLng = destination_coordinates[0];

        const line = L.polyline(
          [
            [lat, lng],
            [destLat, destLng],
          ],
          { color: "#54AF3A", weight: 1.5, opacity: 0.3 },
        );

        outflowLayerRef.current.addLayer(line).addTo(mapRef.current);
      });
    }
  };

  return (
    <>
      <div id="locationDashboardMap" className="z-depth-2" />
      <div id="leaflet-sidebar-content" />
    </>
  );
};

export default Map;
